import React from 'react';

// Stylized inline-SVG payment scheme & compliance badges.
// Frameless versions sit directly on the glass card.
// Apple Pay keeps a small white pill so its black icon stays sharp.

const ApplePill = ({ children, label }) => (
  <div
    className="flex items-center justify-center rounded bg-white px-2 py-1 shadow-sm"
    aria-label={label}
    title={label}
  >
    {children}
  </div>
);

const Visa = () => (
  <span className="font-bold italic text-[#1A1F71] text-[18px] tracking-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
    VISA
  </span>
);

const Mastercard = () => (
  <div className="flex items-center gap-0">
    <div className="w-5 h-5 rounded-full bg-[#EB001B] -mr-1.5" />
    <div className="w-5 h-5 rounded-full bg-[#F79E1B] mix-blend-multiply" />
  </div>
);

const Maestro = () => (
  <div className="flex items-center gap-0">
    <div className="w-5 h-5 rounded-full bg-[#0099DF] -mr-1.5" />
    <div className="w-5 h-5 rounded-full bg-[#ED0006] mix-blend-multiply" />
  </div>
);

const Amex = () => (
  <div className="flex items-center justify-center w-11 h-6 bg-[#006FCF] rounded-sm">
    <span className="text-white font-bold text-[7px] leading-tight text-center" style={{ fontFamily: 'Arial, sans-serif' }}>
      AMERICAN<br />EXPRESS
    </span>
  </div>
);

const ApplePay = () => (
  <div className="flex items-center gap-0.5 text-black">
    <svg width="12" height="13" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.6 13.2c0-2.6 2.1-3.8 2.2-3.9-1.2-1.7-3-2-3.7-2-1.6-.2-3 .9-3.8.9s-2-.9-3.3-.9c-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.6 1.3 10.1.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.3-1.2 3.1-2.5.6-.9.9-1.7 1.1-2.3-2.5-1-2.5-3.2-2.5-4.5zM15 5.4c.7-.8 1.2-2 1-3.2-1.1.1-2.4.7-3.1 1.6-.7.7-1.3 2-1.1 3.1 1.2.1 2.4-.6 3.2-1.5z" />
    </svg>
    <span className="font-bold text-[12px]" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>Pay</span>
  </div>
);

const GooglePay = () => (
  <div className="flex items-center gap-0.5">
    <span className="font-medium text-[13px]" style={{ fontFamily: 'Arial, sans-serif' }}>
      <span className="text-[#4285F4]">G</span>
      <span className="text-[#EA4335]">o</span>
      <span className="text-[#FBBC05]">o</span>
      <span className="text-[#4285F4]">g</span>
      <span className="text-[#34A853]">l</span>
      <span className="text-[#EA4335]">e</span>
    </span>
    <span className="font-medium text-[13px] text-gray-800">Pay</span>
  </div>
);

const SamsungPay = () => (
  <div className="flex items-center gap-1">
    <span className="font-bold text-[11px] text-[#1428A0] italic" style={{ fontFamily: 'Arial, sans-serif' }}>SAMSUNG</span>
    <span className="font-bold text-[11px] text-gray-800">Pay</span>
  </div>
);

const Contactless = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="rotate-90 text-[#1F4E79]">
    <path d="M5.5 8.5C7 6 7.5 4.5 12 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M5.5 12C7.5 8.5 9 7 13.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M5.5 15.5C8 11 10.5 9.5 15 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M5.5 19C8.5 13.5 12 12 16.5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PciDss = () => (
  <div className="flex flex-col items-center leading-none">
    <span className="font-bold text-[10px] text-[#0070BB]" style={{ fontFamily: 'Arial, sans-serif', letterSpacing: '0.3px' }}>
      PCI DSS
    </span>
    <span className="text-[6px] text-gray-700 font-semibold mt-0.5 tracking-wider">COMPLIANT</span>
  </div>
);

// Frameless schemes (no pill background)
const SCHEMES = [
  { Comp: Visa,        label: 'Visa' },
  { Comp: Mastercard,  label: 'Mastercard' },
  { Comp: Maestro,     label: 'Maestro' },
  { Comp: Amex,        label: 'American Express' },
  { Comp: GooglePay,   label: 'Google Pay' },
  { Comp: SamsungPay,  label: 'Samsung Pay' },
  { Comp: Contactless, label: 'Contactless payments' },
];

const PaymentSchemes = ({ testId = 'payment-schemes' }) => (
  <div className="flex justify-center" data-testid={testId}>
    <div
      className="rounded-xl backdrop-blur-md bg-white/15 border border-white/25 shadow-[0_10px_30px_-6px_rgba(31,78,121,0.20),0_4px_10px_-3px_rgba(31,78,121,0.10),inset_0_1px_0_rgba(255,255,255,0.55)] px-5 py-4 w-fit max-w-full"
      data-testid="payment-schemes-card"
    >
      <p className="text-center text-[10px] font-bold text-[#1F4E79] uppercase tracking-[0.18em] mb-3">
        Compatible with all major card networks
      </p>

      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3" data-testid="schemes-row">
        {SCHEMES.map(({ Comp, label }, i) => (
          <div key={i} aria-label={label} title={label} className="flex items-center justify-center">
            <Comp />
          </div>
        ))}
        {/* Apple Pay keeps a small white pill */}
        <ApplePill label="Apple Pay">
          <ApplePay />
        </ApplePill>
        {/* PCI DSS — final badge */}
        <div aria-label="PCI DSS Compliant" title="PCI DSS Compliant" className="flex items-center justify-center">
          <PciDss />
        </div>
      </div>
    </div>
  </div>
);

export default PaymentSchemes;
